<template>
    <!-- Pagination -->
    <nav
        class="mx-0 flex w-full max-w-7xl items-start justify-between border-t border-gray-200 sm:mx-4"
        aria-label="Pagination"
    >
        <div
            :key="currentPage"
            class="-mt-px flex w-0 flex-1"
        >
            <router-link
                :to="{
                    name: route.name,
                    params: {
                        ...route.params,
                        page: currentPage > 1 ? (currentPage - 1).toString() : '1',
                    },
                    query: route.query,
                }"
                :disabled="currentPage <= 1"
                :class="[
                    currentPage <= 1
                        ? 'cursor-not-allowed text-gray-400 hover:text-gray-400'
                        : 'text-gray-500 hover:border-gray-400 hover:text-gray-700',
                    'inline-flex items-center border-t-2 border-transparent pt-4 pr-1 text-sm font-medium select-none',
                ]"
            >
                <MdiArrowLeft
                    class="mx-3 h-5 w-5"
                    aria-hidden="true"
                />
                <span class="hidden sm:inline">{{ t('paginatorPrev') }}</span>
            </router-link>
        </div>
        <div class="-mt-px flex flex-1">
            <div class="mx-auto w-44 pt-2 select-none">
                <Listbox
                    v-model="currentPage"
                    as="template"
                >
                    <div class="relative">
                        <ListboxButton
                            :class="[
                                end === 1 ? 'text-gray-400' : 'text-gray-800',
                                'relative w-full cursor-default rounded-lg bg-gray-50 py-2 pr-10 pl-3 text-left shadow-md focus:outline-hidden focus-visible:border-sky-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm',
                            ]"
                            :disabled="end === 1"
                        >
                            <div class="truncate font-medium">
                                {{ t('paginatorPage') }} {{ currentPage }}
                            </div>
                            <div class="absolute inset-y-0 right-0 flex items-center pr-2">
                                <MdiChevronDown
                                    class="h-5 w-5 text-gray-500"
                                    aria-hidden="true"
                                />
                            </div>
                        </ListboxButton>
                        <ListboxOptions
                            class="absolute bottom-0 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base text-gray-800 shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
                        >
                            <ListboxOption
                                v-for="page in end"
                                :key="page"
                                :value="page"
                                as="template"
                                @click="
                                    router.push({
                                        name: route.name,
                                        params: {
                                            ...route.params,
                                            page: page.toString(),
                                        },
                                        query: route.query,
                                    })
                                "
                            >
                                <div
                                    :class="[
                                        currentPage === page ? 'font-bold' : 'font-normal',
                                        'relative cursor-pointer py-2 pr-4 pl-10 select-none hover:bg-gray-100',
                                    ]"
                                >
                                    <div class="truncate">{{ page }}</div>
                                    <span
                                        v-if="currentPage === page"
                                        class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500"
                                    >
                                        <MdiCheck
                                            class="h-5 w-5"
                                            aria-hidden="true"
                                        />
                                    </span>
                                </div>
                            </ListboxOption>
                        </ListboxOptions>
                    </div>
                </Listbox>
            </div>
        </div>
        <div class="-mt-px flex w-0 flex-1 justify-end">
            <router-link
                :to="{
                    name: route.name,
                    params: {
                        ...route.params,
                        page: currentPage < end ? (currentPage + 1).toString() : end.toString(),
                    },
                    query: route.query,
                }"
                :disabled="currentPage >= end"
                :class="[
                    currentPage >= end
                        ? 'cursor-not-allowed text-gray-400 hover:text-gray-400'
                        : 'text-gray-500 hover:border-gray-400 hover:text-gray-700',
                    'inline-flex items-center border-t-2 border-transparent pt-4 pr-1 text-sm font-medium select-none',
                ]"
            >
                <span class="hidden sm:inline">{{ t('paginatorNext') }}</span>
                <MdiArrowRight
                    class="mx-3 h-5 w-5"
                    aria-hidden="true"
                />
            </router-link>
        </div>
    </nav>
</template>

<script setup>
import { useRouter, useRoute } from 'vue-router';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';

import {
    MdiArrowLeft,
    MdiArrowRight,
    MdiCheck,
    MdiChevronDown,
} from 'materialdesignicons-vue3/icons/';

const props = defineProps({
    current: {
        type: Number,
        required: true,
    },
    end: {
        type: Number,
        required: true,
    },
});

const currentPage = props.current;
const end = props.end;
const router = useRouter();
const route = useRoute();
</script>
